import * as yup from "yup";
import { Images } from "../../../../config/images";
import TopImage from "../../common/TopImage";
import CustomInput from "../../../common/CustomInput";
import { useRef, useState, useEffect } from "react";
import { Field, Form, Formik } from "formik";
import { Button } from "react-bootstrap";
import { useCreateStudent } from "../../../../hooks/school/student/useCreateStudent";
import BackButton from "../../TeacherAdmin/Goback/GoBack";
import { useGetTeachers } from "../../../../hooks/school/teacher/useGetTeachers";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import SuccessModal from "../../../common/SuccessModal";
import ImportModal from "../../../common/ImportModal";
import { useImportStudents } from "../../../../hooks/import/school/useImportStudents";
import DownloadTemplate from "../../../common/DownloadTemplate";
import { useToggleStudentStatus } from "../../../../hooks/school/student/useToggleStudentStatus";

export const createStudentValidationSchema = yup.object().shape({
  firstName: yup
    .string()
    .required("First Name is required")
    .max(150, "First Name must be at most 150 characters")
    .matches(/^[A-Za-z ]+$/, "First name must contain only letters"),
  lastName: yup
    .string()
    .required("Last Name is required")
    .max(150, "Last Name must be at most 150 characters")
    .matches(/^[A-Za-z ]+$/, "Last name must contain only letters"),
  parent_email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required")
    .max(100, "Email must be at most 100 characters"),
  age: yup
    .number()
    .required("Age is required")
    .min(4)
    .max(20, "Age must be between 4 and 20"),
  username: yup
    .string()
    .required("School Registered ID is required")
    .max(20, "School Registered ID must be at most 20 characters")
    .matches(
      /^[A-Za-z0-9-]+$/,
      "Only letters, numbers, and dashes are allowed",
    ),
});

const CreateStudent = ({ type = "school" }) => {
  const firstNameRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportSuccessModal, setShowImportSuccessModal] = useState(false);
  const { loading, createStudent, error } = useCreateStudent();
  const {
    toggleStudentStatus,
    loading: toggleLoading,
    error: toggleError,
  } = useToggleStudentStatus();
  const {
    importStudents,
    loading: importLoading,
    error: importError,
    success: importSuccess,
  } = useImportStudents();
  const {
    teachers,
    metaData,
    getTeachers,
    loading: loadingTeachers,
    error: teachersError,
  } = useGetTeachers();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const searchTimerRef = useRef(null);
  const [toggleFailed, setToggleFailed] = useState(false);

  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  useEffect(() => {
    getTeachers(1, ITEMS_PER_PAGE, false); // replace data on first load
  }, []);

  const initialValues = {
    firstName: "",
    lastName: "",
    parent_email: "",
    age: "",
    username: "",
    assigned_to: "",
  };

  const handleSubmit = async (values, { resetForm }) => {
    setToggleFailed(false);
    await createStudent(
      {
        name: `${values.firstName} ${values.lastName}`,
        parent_email: values.parent_email,
        username: values.username,
        age: values.age,
        password: "",
        assigned_to: values.assigned_to || null,
      },
      async (createdStudent) => {
        await toggleStudentStatus(createdStudent.id, (success) => {
          if (success) {
            resetForm();
            setShowModal(true);
          } else {
            setToggleFailed(true);
            resetForm();
            setShowModal(true);
          }
        });
      },
    );
  };

  return (
    <div>
      <div className="">
        <BackButton />
      </div>
      <TopImage imageSrc={Images.TopImage} text="Add Students" />

      <div className="d-flex justify-content-end align-items-center gap-3 pt-3">
        <div className="w-10">
          <Button
            className="custom-primary-button"
            onClick={() => setShowImportModal(true)}
          >
            Import
          </Button>
        </div>

        <DownloadTemplate type="students" buttonMessage="Download Template" />
      </div>

      <div className="custom-card mt-4">
        <Formik
          initialValues={initialValues}
          validationSchema={createStudentValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="d-flex flex-column gap-3">
              <div className="row g-3">
                <div className="col-md-6 ">
                  <CustomInput
                    label="First Name"
                    name="firstName"
                    type="text"
                    placeholder="Enter first name"
                    className="custom-primary-input"
                    layout="stacked"
                    ref={firstNameRef}
                  />
                </div>
                <div className="col-md-6">
                  <CustomInput
                    label="Last Name"
                    name="lastName"
                    type="text"
                    placeholder="Enter last name"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>

                <div className="col-md-6">
                  <CustomInput
                    label="Student ID"
                    name="username"
                    type="text"
                    placeholder="Enter Student ID"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>

                <div className="col-md-6">
                  <CustomInput
                    label="Age"
                    name="age"
                    type="number"
                    placeholder="Enter Age"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>
                {type === "school" && (
                  <div className="col-md-12">
                    <label className="mb-1">Assign Teacher</label>
                    <div className="scroll-wrapper">
                      <Field name="assigned_to">
                        {({ field, form }) => {
                          const selectedTeacher = teachers?.find(
                            (t) => t.id === field.value,
                          );

                          return (
                            <div className="custom-dropdown-container">
                              {/* Selected Box */}
                              <div
                                className="custom-dropdown-select custom-primary-input"
                                tabIndex={0}
                                role="button"
                                onClick={() => {
                                  if (!dropdownOpen) {
                                    setSearchTerm("");
                                    setPage(1);
                                    getTeachers(1, ITEMS_PER_PAGE, false);
                                  }
                                  setDropdownOpen(!dropdownOpen);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    if (!dropdownOpen) {
                                      setSearchTerm("");
                                      setPage(1);
                                      getTeachers(1, ITEMS_PER_PAGE, false);
                                    }
                                    setDropdownOpen(!dropdownOpen);
                                  }
                                }}
                              >
                                {selectedTeacher
                                  ? `${selectedTeacher.firstName} ${selectedTeacher.lastName}`
                                  : "Select Teacher"}
                              </div>

                              {/* Options List */}
                              {dropdownOpen && (
                                <div className="custom-dropdown-options">
                                  {/* Search Input */}
                                  <div className="custom-dropdown-search">
                                    <input
                                      type="text"
                                      className="custom-dropdown-search-input"
                                      placeholder={"Search teacher..."}
                                      value={searchTerm}
                                      // disabled={loadingTeachers}
                                      autoFocus
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => {
                                        if (e.key === "Tab") {
                                          setDropdownOpen(false);
                                        }
                                      }}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setSearchTerm(value);
                                        clearTimeout(searchTimerRef.current);
                                        searchTimerRef.current = setTimeout(
                                          () => {
                                            setPage(1);
                                            getTeachers(
                                              1,
                                              ITEMS_PER_PAGE,
                                              false,
                                              value,
                                            );
                                          },
                                          300,
                                        );
                                      }}
                                    />
                                  </div>

                                  <div
                                    className="custom-dropdown-scroll-area"
                                    onScroll={(e) => {
                                      const bottomReached =
                                        e.target.scrollTop +
                                          e.target.clientHeight >=
                                        e.target.scrollHeight - 2;

                                      const hasMore =
                                        metaData?.current_page <
                                        metaData?.last_page;

                                      if (
                                        bottomReached &&
                                        hasMore &&
                                        !loadingTeachers
                                      ) {
                                        const next = page + 1;
                                        setPage(next);
                                        getTeachers(
                                          next,
                                          ITEMS_PER_PAGE,
                                          true,
                                          searchTerm,
                                        );
                                      }
                                    }}
                                  >
                                    {/* Loading State */}
                                    {loadingTeachers ? (
                                      <div className="custom-dropdown-option disabled">
                                        Loading...
                                      </div>
                                    ) : (
                                      <>
                                        {/* Error State */}
                                        {teachersError && (
                                          <div className="custom-dropdown-option disabled text-danger">
                                            {teachersError}
                                          </div>
                                        )}

                                        {/* Empty State */}
                                        {!teachersError &&
                                          (!teachers ||
                                            teachers.length === 0) && (
                                            <div className="custom-dropdown-option disabled">
                                              No teachers found
                                            </div>
                                          )}

                                        {/* Teacher Options */}
                                        {teachers?.map((teacher) => (
                                          <div
                                            key={teacher.id}
                                            className="custom-dropdown-option"
                                            onClick={() => {
                                              form.setFieldValue(
                                                "assigned_to",
                                                teacher.id,
                                              );
                                              setDropdownOpen(false);
                                            }}
                                          >
                                            {teacher.firstName}{" "}
                                            {teacher.lastName}
                                          </div>
                                        ))}
                                      </>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }}
                      </Field>
                    </div>
                  </div>
                )}
              </div>

              <CustomInput
                label="Parent Email Address"
                name="parent_email"
                type="parent_email"
                placeholder="Enter parent email address"
                className="custom-primary-input"
                layout="stacked"
              />
              <div>
                {error && <div className="custom-error pb-2">{error}</div>}

                <Button
                  type="submit"
                  className="custom-primary-button"
                  disabled={isSubmitting || loading || toggleLoading}
                >
                  {isSubmitting || loading
                    ? "Creating..."
                    : toggleLoading
                      ? "Activating..."
                      : "Add"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <SuccessModal
        type={toggleFailed ? "warning" : "success"}
        showModal={showModal}
        handleClose={() => {
          setShowModal(false);
          setToggleFailed(false);
        }}
        title={toggleFailed ? "Student Created" : "Student Added Successfully!"}
        message={
          toggleFailed
            ? "Student has been created successfully but we were unable to activate the student. Please activate the student manually from the students list."
            : "Student has been added to your school’s dashboard. They can now join classes, access learning materials, and track their progress."
        }
        primaryButtonText="Add Another"
        secondaryButtonText="Students List"
        primaryButtonLink={
          type === "school"
            ? "/school-admin/students/create"
            : "/teacher/student/create"
        }
        secondaryButtonLink={
          type === "school" ? "/school-admin/students" : "/teacher/student"
        }
      />
      <ImportModal
        show={showImportModal}
        handleClose={() => setShowImportModal(false)}
        title="Import Students"
        description="Upload a CSV or Excel file to import students into your school."
        onImport={async (file) => {
          await importStudents(file, () => {
            setShowImportModal(false);
            setShowImportSuccessModal(true);
          });
        }}
        loading={importLoading}
        error={importError}
      />
      <SuccessModal
        type="success"
        showModal={showImportSuccessModal}
        handleClose={() => setShowImportSuccessModal(false)}
        title="Import Started!"
        message="Your import has been started. Once imported, we will send the email to you."
        primaryButtonText="OK"
        onPrimaryClick={() => setShowImportSuccessModal(false)}
      />
    </div>
  );
};

export default CreateStudent;
